
import {
  Component,
  Provide,
  Mixins,
  Watch,
  Prop
} from "vue-property-decorator";
import { CreateElement } from "vue";
import _ from "@/boot/lodash";

// Mixins
import { FilteringSortingTableStore } from "@/mixins/tableWithFilteringAndSortingStore";
import Context from "@/mixins/context";

// Enums/Data
import { DataModules } from "@/store/modules/data";
import { Sorters } from "@/data/table";

// Types
import { IProvider } from "@/models/providers";
import {
  IContractCategoriesData,
  IContractCategoriesMethods
} from "@/models/providers/contractCategories";
import { ITableConfig } from "@/models/tables";
import { IClient } from "@/models/clients";
import { IMenuRoute } from "@/models/routes";
import { Location } from "vue-router";

@Component
export default class ContractCategoriesProvider
  extends Mixins(FilteringSortingTableStore, Context)
  implements IProvider {
  // Props

  @Prop({ default: null })
  public clientId!: IClient["id"];

  @Prop({ default: "CCP" })
  public declare id: string;

  @Prop({ default: 0 })
  public declare limit: number;

  @Prop({ default: true })
  public declare handleLoading: boolean;

  @Prop({ default: true })
  public declare handleRequestError: boolean;

  @Prop({ default: () => ({}) })
  public declare manualFilters: Record<string, string | number | boolean>;

  @Prop({ default: () => ({}) })
  public loaderAttrs!: Record<string, any>;

  @Prop({ default: "" })
  public routeName!: Location["name"];

  @Prop({ default: null })
  public goBackRoute!: Location;

  // Data

  public loading = true;
  public isLoading = true;
  public isReloading = false;
  public hasRequestError = false;

  public dataModule = DataModules.CONTRACTS_CATEGORIES;
  public isProcessing = false;
  public defaults: ITableConfig = {
    page: 1,
    limit: this.limit,
    sort: {
      field: "created_at",
      direction: Sorters.DESCENDING
    },
    filters: []
  };

  // Computed

  public get clientFilter() {
    return this.clientId ? { "filter[clients.id]": this.clientId } : {};
  }

  public get hasProducts(): boolean {
    return !!this.table.data.length;
  }

  public get canShowMore(): boolean {
    return this.table.total > this.table.data.length;
  }

  public get routes(): IMenuRoute[] {
    return _.map(this.table.data, category => ({
      label: category.name_translated,
      onActiveClick: this.goBackRoute
        ? () => this.$router.push(this.goBackRoute).catch(err => err)
        : undefined,
      to: {
        name: this.toRouteName,
        params: {
          categoryId: category.id
        }
      }
    }));
  }

  public get toRouteName() {
    return this.routeName
      ? this.routeName
      : this.isAdmin
      ? this.clientId
        ? "admin__clientAllCategoryProducts"
        : "admin__allCategoryProducts"
      : "client__categoryProducts";
  }

  // Methods

  public loadData = this.getData;

  public getStoreParams(params: any): Promise<any> {
    // Overrides mixin method
    return this.$_.merge(
      {
        params: {
          ...this.clientFilter,
          ...this.manualFilters
        }
      },
      params
    );
  }

  public reloadData() {
    return this.getData({ ...this.config, ignoreStored: true });
  }

  // Life-cycle Methods

  public created() {
    this.getData();
  }

  public render(h: CreateElement) {
    // Handle loading
    if (this.handleLoading && this.loading)
      return h("is-loading", { props: { if: true } });
    // Handle request error
    if (this.handleRequestError && this.requestError)
      return h("request-error", {
        props: { isLoading: this.table.loading && this.requestError },
        on: { click: () => this.getData() }
      });
    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $categoriesData: this.providerData(),
        $categoriesMethods: this.providerMethods
      })
    );
  }

  @Watch("table.data")
  public onRoutesChange() {
    this.$emit("updateCategoriesCount", this.routes.length);
  }

  // Provide

  @Provide("categoriesProviderMethods")
  public get providerMethods(): IContractCategoriesMethods {
    return {
      onPageChange: this.onPageChange,
      reloadData: this.reloadData
    };
  }

  @Provide("categoriesProviderData")
  public providerData(): IContractCategoriesData {
    return {
      categories: this.table.data,
      hasRequestError: this.requestError,
      isLoading: this.table.loading,
      isReloading: this.table.loading,
      limit: this.table.limit,
      page: this.table.page,
      routes: this.routes,
      total: this.table.total
    };
  }
}
